import { useMemo } from "react";
import { Pie } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { formatSalesValue } from "../../utils/formatUtils";

ChartJS.register(ArcElement, Tooltip, Legend);

const PurchaseGroupPieChart = ({ purchaseItems }) => {
    const chartData = useMemo(() => {
        const normKey = (raw) => (raw || "Unknown").toString().trim().replace(/\s+/g, " ").toLowerCase();
        const cleanDisplay = (raw) => (raw || "Unknown").toString().trim().replace(/\s+/g, " ");

        const grouped = purchaseItems.reduce((acc, item) => {
            const key = normKey(item.GroupName);
            const total = parseFloat(item.Total) || 0;

            if (!acc[key]) {
                acc[key] = { label: cleanDisplay(item.GroupName), value: 0 };
            }
            acc[key].value += total;
            return acc;
        }, {});

        const entries = Object.values(grouped);
        const labels = entries.map((e) => e.label);
        const values = entries.map((e) => e.value);

        const colors = [
            "#FF5A5F", "#F15BB5", "#2EC4B6", "#F9C74F", "#48CAE4", "#F9844A", "#3A86FF", "#52B788", "#6C5CE7", "#B089C6",
            "#E76F51", "#43AA8B", "#5E60CE", "#F4A261", "#A06CD5", "#4D908E", "#577590", "#90BE6D", "#F3722C", "#C77DFF",
            "#4895EF", "#B56576", "#7B2CBF", "#FF9F1C", "#70C1B3", "#588157", "#F28482", "#4361EE", "#A8DADC", "#BC6C25",
            "#9B5DE5", "#00BBF9", "#00F5D4", "#F6BD60", "#84A59D", "#E5989B", "#8E9AAF", "#6D597A", "#2A9D8F", "#FF6B6B",
            "#5FA8D3", "#A44A3F", "#8AC926", "#C08497", "#4EA8DE", "#F7B267", "#7FBC8C", "#9D4EDD", "#E07A5F", "#669BBC",
            "#A3B18A", "#C8553D", "#6A994E", "#9381FF", "#F2CC8F", "#5C7AEA", "#B8C0FF", "#D08C60", "#7DCFB6", "#B565A7"
        ];

        return {
            labels,
            datasets: [
                {
                    data: values,
                    backgroundColor: labels.map((_, i) => colors[i % colors.length]),
                    borderColor: "#fff",
                    borderWidth: 2,
                },
            ],
        };
    }, [purchaseItems]);

    const leaderLineDataLabelPlugin = useMemo(
        () => ({
            id: "leaderLineDataLabels",
            afterDatasetsDraw(chart) {
                const { ctx, chartArea } = chart;
                const meta = chart.getDatasetMeta(0);
                const dataset = chart.data.datasets[0];
                const labels = chart.data.labels;
                if (!meta || !dataset) return;

                const cx = meta.data[0]?.x;
                const cy = meta.data[0]?.y;
                const outerRadius = meta.data[0]?.outerRadius;
                if (cx == null || cy == null || !outerRadius) return;

                const LABEL_X_OFFSET = outerRadius + 60;
                const EDGE_GAP = 5;
                const FONT_SIZE = 10;
                const TEXT_LINE_GAP = 5;
                const MIN_LABEL_BLOCK_GAP = 2;

                const LABEL_BLOCK_HEIGHT = TEXT_LINE_GAP * 2 + FONT_SIZE;
                const MIN_LINE_HEIGHT = LABEL_BLOCK_HEIGHT + MIN_LABEL_BLOCK_GAP;

                const topBound = chartArea.top + 10;
                const bottomBound = chartArea.bottom - 10;
                const availableHeight = bottomBound - topBound;

                // Build raw anchor points for every slice
                const points = meta.data.map((arc, index) => {
                    const angle = (arc.startAngle + arc.endAngle) / 2;
                    const edgeX = cx + Math.cos(angle) * outerRadius;
                    const edgeY = cy + Math.sin(angle) * outerRadius;
                    const onRight = Math.cos(angle) >= 0;
                    return {
                        index,
                        angle,
                        edgeX,
                        edgeY,
                        onRight,
                        naturalY: cy + Math.sin(angle) * (outerRadius + 20),
                        value: dataset.data[index],
                    };
                });

                const resolveSide = (sidePoints) => {
                    if (!sidePoints.length) return sidePoints;

                    const sorted = [...sidePoints].sort((a, b) => a.naturalY - b.naturalY);

                    // Calculate total needed height
                    const totalNeededHeight = (sorted.length - 1) * MIN_LINE_HEIGHT;

                    // Calculate line height - fit within available space
                    let lineHeight;
                    if (sorted.length <= 1) {
                        lineHeight = MIN_LINE_HEIGHT;
                    } else {
                        lineHeight = Math.max(
                            MIN_LINE_HEIGHT,
                            Math.min(totalNeededHeight, availableHeight) / (sorted.length - 1)
                        );
                    }

                    // First pass: ensure minimum spacing
                    for (let i = 1; i < sorted.length; i++) {
                        const prev = sorted[i - 1];
                        const cur = sorted[i];
                        cur.naturalY = Math.max(cur.naturalY, prev.naturalY + lineHeight);
                    }

                    // Calculate current span and center it in available space
                    const currentSpan = sorted[sorted.length - 1].naturalY - sorted[0].naturalY;
                    const idealStart = topBound + (availableHeight - currentSpan) / 2;
                    const shift = idealStart - sorted[0].naturalY;

                    sorted.forEach((p) => (p.naturalY += shift));

                    // Clamp to bounds as a last resort
                    const finalBlockTop = sorted[0].naturalY;
                    const finalBlockBottom = sorted[sorted.length - 1].naturalY;

                    if (finalBlockBottom > bottomBound) {
                        const overflow = finalBlockBottom - bottomBound;
                        sorted.forEach((p) => (p.naturalY -= overflow));
                    }
                    if (finalBlockTop < topBound) {
                        const underflow = topBound - finalBlockTop;
                        sorted.forEach((p) => (p.naturalY += underflow));
                    }

                    // If still overflowing after clamping, compress
                    const afterClampSpan = sorted[sorted.length - 1].naturalY - sorted[0].naturalY;
                    if (afterClampSpan > availableHeight && sorted.length > 1) {
                        const scale = availableHeight / afterClampSpan;
                        const originY = sorted[0].naturalY;
                        sorted.forEach((p) => {
                            p.naturalY = topBound + (p.naturalY - originY) * scale;
                        });
                    }

                    return sorted;
                };

                const rightPoints = resolveSide(points.filter((p) => p.onRight));
                const leftPoints = resolveSide(points.filter((p) => !p.onRight));

                ctx.save();
                ctx.textBaseline = "middle";
                ctx.lineWidth = 1;
                ctx.strokeStyle = "#aaa";

                [...rightPoints, ...leftPoints].forEach((p) => {
                    const labelX = cx + (p.onRight ? LABEL_X_OFFSET : -LABEL_X_OFFSET);
                    const labelY = p.naturalY;
                    const elbowX = cx + (p.onRight ? outerRadius + EDGE_GAP : -(outerRadius + EDGE_GAP));

                    ctx.strokeStyle = "#aaa";
                    ctx.beginPath();
                    ctx.moveTo(p.edgeX, p.edgeY);
                    ctx.lineTo(elbowX, labelY);
                    ctx.lineTo(labelX, labelY);
                    ctx.stroke();

                    ctx.textAlign = p.onRight ? "left" : "right";
                    const textX = labelX + (p.onRight ? 4 : -4);

                    // Name (top line)
                    ctx.font = `${FONT_SIZE}px sans-serif`;
                    ctx.fillStyle = "#000";
                    ctx.fillText(`${labels[p.index]}`, textX, labelY - TEXT_LINE_GAP);

                    // Amount (bottom line)
                    ctx.font = `${FONT_SIZE}px sans-serif`;
                    ctx.fillStyle = "#555";
                    ctx.fillText(formatSalesValue(p.value), textX, labelY + TEXT_LINE_GAP);
                });

                ctx.restore();
            },
        }),
        []
    );

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 20,
                bottom: 20,
                left: 180,
                right: 180,
            },
        },
        plugins: {
            legend: { display: false },
        },
    };

    if (!purchaseItems.length) {
        return <p className="text-center">No data available.</p>;
    }

    return (
        <div className="w-full h-[500px] border bg-white rounded-md shadow-md overflow-hidden flex justify-center items-center">
            <Pie
                data={chartData}
                options={options}
                plugins={[leaderLineDataLabelPlugin]}
            />
        </div>
    );
};

export default PurchaseGroupPieChart;